import React, { useRef, useState, useEffect } from 'react'
import {
  ListItem,
  Box,
  Typography,
  Chip,
  Tooltip,
  Paper,
  IconButton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  setSearchDocument,
  setSearchDocumentSourceUrl,
  setSearchIsModalMobileVisible,
  setSearchLoadingDocument,
  setSearchSelectedCitation,
  toggleSearchBookmark
} from '../../../redux/actions/searchAction'
import { backendClient } from '../../../api/backend'
import { useSnackbar } from 'notistack'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import GavelIcon from '@mui/icons-material/Gavel'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'

import { getCitationSectionTitle, getDocTypeColor, isMobile } from '../../../utils/utils'
import ReactGA from 'react-ga4'
import { tracker } from '../../../analytics'
import { usePdfFocus } from '../../../components/pdf-viewer/pdfContext'
import { useSearchTokens } from '../searchDesignTokens'

const JurisprudenceTitle = ({ title, country, dk }) => {
  if (!title) return null;
  if (country === 'CH') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            color: dk.text1,
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: dk.goldSub,
            padding: '2px 6px',
            borderRadius: '4px',
            border: `1px solid ${dk.goldBdr}`
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  }
  const parts = title.split(' - ').map(part => part.split(' : '));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {parts.map(([label, value], index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: dk.text3,
              fontSize: '0.75rem',
              fontWeight: 500,
              mr: 0.5
            }}
          >
            {label}:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: dk.text1,
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: dk.goldSub,
              padding: '2px 6px',
              borderRadius: '4px',
              border: `1px solid ${dk.borderLight}`
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const SearchCitation = ({ citation }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const dk = useSearchTokens()
  const dispatch = useDispatch()
  const countryPref = useSelector(state => state.user?.countryPreference)
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA')
  const searchDocument = useSelector(state => state.search.searchDocument)
  const bookmarkedDocIds = useSelector(state => state.search.bookmarkedDocIds || [])
  const { setPdfFocusState } = usePdfFocus()

  const resolveBlobName = (metadata, preferType) => {
    const lang = metadata.language || 'ar'
    const files = metadata.files

    if (files) {
      if (preferType) {
        // Prefer specific type (e.g., 'pdf' for source document)
        if (files[preferType]?.[lang]) return files[preferType][lang]
        if (files[preferType]?.ar) return files[preferType].ar
        if (files[preferType]) {
          const first = Object.values(files[preferType])[0]
          if (first) return first
        }
      }
      // Try html first, then pdf
      for (const type of ['html', 'pdf']) {
        if (files[type]?.[lang]) return files[type][lang]
        if (files[type]?.ar) return files[type].ar
        if (files[type]) {
          const first = Object.values(files[type])[0]
          if (first) return first
        }
      }
    }

    return null
  }

  const handleShowSourceDocument = async (e) => {
    e.stopPropagation()
    const metadata = citation?.metadata || {}

    const blobName = resolveBlobName(metadata, 'pdf')
    if (!blobName) return

    try {
      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME
      const data = await backendClient.getDocumentSas(containerName, blobName)
      if (data?.blob_url_with_sas) {
        window.open(data.blob_url_with_sas, '_blank')
      }
    } catch (error) {
      console.error('Error fetching source document:', error)
      enqueueSnackbar(t('document_fetch_error'), { variant: 'error' })
    }
  }

  const selectedCitation = useSelector(
    state => state.search.searchSelectedCitation
  )

  const isSelected = selectedCitation?.id === citation?.id
  const docType = citation?.metadata?.doc_type
  const docTypeColor = getDocTypeColor(docType)
  const documentExternalId = citation?.metadata?.document_id || citation?.id
  const isBookmarked = bookmarkedDocIds.includes(documentExternalId)

  const formatDate = dateString => {
    if (!dateString) return '-';

    const isoDateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}(|T\d{2}:\d{2}:\d{2})$/;
    if (isoDateRegex.test(dateString)) {
      const date = new Date(dateString.replace(/\//g, '-'));
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }

    const dmyRegex = /^(\d{2})[-/]?(\d{2})[-/]?(\d{4})$/;
    const dmyMatch = dateString.match(dmyRegex);
    if (dmyMatch) {
      const [ , day, month, year ] = dmyMatch;
      const date = new Date(`${year}-${month}-${day}`);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }

    const dmySlash = dateString.split('/');
    if (dmySlash.length === 3) {
      const [day, month, year] = dmySlash;
      const date = new Date(`${year}-${month}-${day}`);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }

    return '-';
  };

  const handleBookmarkClick = (e) => {
    e.stopPropagation()
    const docTypeForBookmark = docType === 'Jurisprudence' || docType === 'case-law' ? 'case-law' : 'legislation'
    dispatch(toggleSearchBookmark(documentExternalId, docTypeForBookmark, citation))
  }

  const handleCitationClick = async doc => {
    const selectedCitation_ = {
      id: `${citation?.id}`,
      metadata: doc?.metadata,
      page_content: doc?.text
    }

    dispatch(setSearchSelectedCitation(selectedCitation_))

    const metadata = doc?.metadata || {}
    const htmlBlobName = resolveBlobName(metadata, 'html')
    if (!htmlBlobName) {
      const pdfBlobName = resolveBlobName(metadata, 'pdf')
      if (pdfBlobName?.toLowerCase().endsWith('.pdf')) {
        setPdfFocusState({
          document_id: metadata.document_id,
          page_number: metadata.page_number || 1,
          citation: {
            ...doc,
            page_content: doc?.page_content || doc?.text || citation?.page_content || citation?.text || '',
          },
        });
      }
    }

    if (metadata.document_id !== searchDocument?.id) {
      const containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME

      const blobName = resolveBlobName(metadata)
      if (!blobName) {
        enqueueSnackbar(t('document_fetch_error'), { variant: 'error' })
        return
      }

      let data = null
      try {
        dispatch(setSearchLoadingDocument(true))
        data = await backendClient.getDocumentSas(containerName, blobName)

        if (!data || !data.blob_url_with_sas) {
          throw new Error('Invalid SAS URL response')
        }

        const url = data.blob_url_with_sas

        dispatch(
          setSearchDocument({
            id: doc?.metadata?.document_id,
            url: url,
            language: doc?.metadata?.language
          })
        )
        dispatch(setSearchDocumentSourceUrl(url))
      } catch (error) {
        dispatch(setSearchLoadingDocument(false))
        enqueueSnackbar(
          error.message.includes('Invalid SAS')
            ? `${t('document_fetch_error')} - Document not found at: ${blobName}`
            : t('document_fetch_error'),
          {
            variant: 'error'
          }
        )
        return
      }
    }

    dispatch(setSearchLoadingDocument(false))

    if (isMobile()) {
      dispatch(setSearchIsModalMobileVisible(true))
    }

    ReactGA.event({
      category: 'Citation',
      action: 'User clicked on a citation',
      label: `Document ID: ${doc.metadata.document_id}, Section Title: ${doc.metadata.section_title}`
    })
    tracker.event({
      category: 'Citation',
      action: 'User clicked on a citation',
      label: `Document ID: ${doc.metadata.document_id}, Section Title: ${doc.metadata.section_title}`
    })
  }

  const titleRef = useRef(null)
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false)

  useEffect(() => {
    const el = titleRef.current
    if (el) {
      setIsTitleOverflowing(el.scrollHeight > el.clientHeight)
    }
  }, [citation?.metadata?.title])

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <ListItem
      data-tour="search-citation-card"
      component={Paper}
      elevation={0}
      onClick={() => handleCitationClick(citation)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '14px 16px',
        cursor: 'pointer',
        backgroundColor: isSelected ? dk.goldSub : dk.surface,
        border: `1px solid ${isSelected ? dk.gold : dk.border}`,
        borderRadius: '10px',
        mb: 1.5,
        transition: 'all 0.2s ease',
        position: 'relative',
        boxShadow: isSelected
          ? `0 0 0 1px ${dk.goldBdr}`
          : 'none',
        '&:hover': {
          backgroundColor: isSelected ? dk.goldSub : dk.hover,
          borderColor: isSelected ? dk.gold : dk.borderHover,
          boxShadow: dk.shadowCard,
        }
      }}
    >
      {/* Top row: doc type badge + bookmark */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Chip
          icon={docType === 'Jurisprudence' || docType === 'case-law' ? <GavelIcon sx={{ fontSize: '0.8rem !important' }} /> : undefined}
          label={(() => {
            if (docType === 'Jurisprudence' || docType === 'case-law') {
              return t(docType);
            }
            const docTypeTranslation = t(docType);
            const docNumber = citation.metadata.doc_number;
            if (docNumber) {
              return `${docTypeTranslation} ${docNumber}`;
            }
            return docTypeTranslation;
          })()}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            color: docTypeColor,
            backgroundColor: `${docTypeColor}12`,
            border: `1px solid ${docTypeColor}35`,
            '& .MuiChip-icon': {
              color: docTypeColor,
            },
            '& .MuiChip-label': {
              px: 1,
            }
          }}
        />

        <IconButton
          size="small"
          onClick={handleBookmarkClick}
          sx={{
            p: 0.5,
            color: isBookmarked ? dk.warning : dk.disabled,
            '&:hover': {
              color: dk.warning,
              backgroundColor: `${dk.warning}14`,
            }
          }}
        >
          {isBookmarked ? (
            <BookmarkIcon sx={{ fontSize: '1.1rem' }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: '1.1rem' }} />
          )}
        </IconButton>
      </Box>

      {/* Title */}
      <Box sx={{ width: '100%', mb: 0.5 }}>
        {(citation.metadata.doc_type === 'Jurisprudence' || citation.metadata.doc_type === 'case-law') ? (
          <JurisprudenceTitle title={citation?.metadata?.title} country={citation?.metadata?.country} dk={dk} />
        ) : (
          <Tooltip title={isTitleOverflowing ? citation?.metadata?.title : ''} arrow disableHoverListener={!isTitleOverflowing}>
            <Typography
              ref={titleRef}
              variant='subtitle2'
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                lineHeight: 1.4,
                color: dk.text1,
                maxWidth: '100%',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {citation?.metadata?.title}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Content snippet */}
      {(citation?.metadata?.section_title?.length > 0 || citation?.text?.length > 0) && (
        <Box sx={{ width: '100%', mb: 1 }}>
          <Typography
            variant='body2'
            sx={{
              color: dk.text2,
              fontSize: '0.82rem',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {citation?.metadata?.section_title?.length > 0 &&
              <span style={{ fontWeight: 500, color: dk.text1 }}>
                {getCitationSectionTitle(citation.metadata.section_title)}
                {' : '}
              </span>
            }
            {truncateText(citation.text, 150)}
          </Typography>
        </Box>
      )}

      {/* Footer metadata row */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 0.5,
          borderTop: `1px solid ${dk.borderLight}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayOutlinedIcon sx={{ color: dk.disabled, fontSize: '0.8rem', mr: 0.5 }} />
            <Typography
              variant='caption'
              sx={{ color: dk.text3, fontSize: '0.72rem', fontWeight: 500 }}
            >
              {(() => {
                const fullDate = formatDate(
                  citation?.metadata?.judgment_date ||
                  citation?.metadata?.issue_date ||
                  citation?.metadata?.publication_date
                )
                return fullDate !== '-' ? fullDate : (citation?.metadata?.issue_year || citation?.metadata?.case_year || '-')
              })()}
            </Typography>
          </Box>

          {citation?.metadata?.last_update && (
            <Tooltip title={t('du_last_update')}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryOutlinedIcon sx={{ color: dk.disabled, fontSize: '0.8rem', mr: 0.5 }} />
                <Typography
                  variant='caption'
                  sx={{ color: dk.text3, fontSize: '0.72rem', fontWeight: 500 }}
                >
                  {t('last_update_short')}: {formatDate(citation.metadata.last_update)}
                </Typography>
              </Box>
            </Tooltip>
          )}

          {citation?.metadata?.page_number && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionOutlinedIcon sx={{ color: dk.disabled, fontSize: '0.8rem', mr: 0.5 }} />
              <Typography
                variant='caption'
                sx={{ color: dk.text3, fontSize: '0.72rem', fontWeight: 500 }}
              >
                {t('page')} {citation.metadata.page_number}
              </Typography>
            </Box>
          )}

          {(citation?.metadata?.court_name || citation?.metadata?.jurisdiction_name) && (
            <Typography
              variant='caption'
              sx={{
                color: dk.text3,
                fontSize: '0.72rem',
                fontWeight: 500,
                backgroundColor: dk.chipBg,
                px: 0.8,
                py: 0.2,
                borderRadius: '4px',
              }}
            >
              {citation.metadata.court_name || citation.metadata.jurisdiction_name}
            </Typography>
          )}
        </Box>

        {/* Source document link — hidden for case law */}
        {docType !== 'Jurisprudence' && docType !== 'case-law' && (citation?.metadata?.source_url || citation?.metadata?.files?.pdf || citation?.metadata?.files?.html) && (
          <Typography
            component="a"
            href={citation?.metadata?.source_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation()
              if (!citation?.metadata?.source_url) {
                e.preventDefault()
                handleShowSourceDocument(e)
              }
            }}
            variant='caption'
            sx={{
              color: dk.gold,
              fontSize: '0.7rem',
              fontWeight: 500,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {citation?.metadata?.source_platform || t('show_source_document')}
          </Typography>
        )}
      </Box>
    </ListItem>
  )
}

export default SearchCitation
